import {Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {fromEvent} from "rxjs/observable/fromEvent";
import {filter, merge, switchMap, takeUntil, tap} from "rxjs/operators";
import {Direction} from "../common-types/direction";

@Directive({
    selector: "[woa-swipable]"
})
/**
 * Directive to detect swipe gestures.
 * Note that currently, swipes in a single specified direction only are supported.
 */
export class SwipableDirective implements OnInit, OnDestroy {
    private static readonly DEFAULT_MAX_SWIPE_DISTANCE: number = 250;

    @Input() swipeEnabled: boolean = true;
    @Input() swipeDirection: Direction = Direction.DOWN;
    @Input() bidirectional: boolean = false;
    @Input() swipeScale: number = 1;
    @Input() swipeEase: boolean = false;
    @Input() swipeRangeProportionOfSize: number = 0.6;
    @Input() swipeRangeDistance: number = Number.MAX_SAFE_INTEGER;
    @Input() swipeMaxDistance: number = SwipableDirective.DEFAULT_MAX_SWIPE_DISTANCE;
    @Input() swipePrimeProportionOfRange: number = 0.6;
    @Input() swipeRestoreDelay: number = 0;
    @Input() swipeDisengageDelay: number;
    @Input() swipeScroller: ElementRef;

    @Output() onSwipe: EventEmitter<Direction> = new EventEmitter();

    private swipestop$: Subject<void> = new Subject<void>();

    private start: Touch;
    private distance: number;
    private backRange: number;
    private forthRange: number;
    private engaged: boolean;

    constructor(private element: ElementRef,
                private renderer: Renderer2) {
    }

    ngOnInit(): void {
        this.renderer.removeClass(this.element.nativeElement, "woa-swipe-engaged");
        this.renderer.removeClass(this.element.nativeElement, "woa-swipe-primed");
        this.element.nativeElement.style.setProperty("--woa-swipe-distance", 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-x-distance", 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-y-distance", 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-proportion", 0);

        if (this.swipeDirection === Direction.NONE) {
            this.swipeEnabled = false;
        }

        if (this.swipeEnabled) {
            const touchstart$: Observable<TouchEvent> = fromEvent(this.element.nativeElement, "touchstart", {passive: true});
            const touchmove$: Observable<TouchEvent> = fromEvent(this.element.nativeElement, "touchmove", {passive: false});
            const touchend$: Observable<TouchEvent> = fromEvent(this.element.nativeElement, "touchend", {passive: true});
            const swipeend$: Subject<void> = new Subject<void>();

            touchstart$.pipe(
                filter((event: TouchEvent) => this.canStartSwipe(event.srcElement)),
                tap((event: TouchEvent) => {
                    this.start = event.targetTouches[0];
                    if (this.swipeDirection === Direction.UP || this.swipeDirection === Direction.DOWN) {
                        const topEdge: number = this.start.clientY;
                        const bottomEdge: number = this.element.nativeElement.clientHeight - this.start.clientY;
                        const maxRange: number = Math.min(this.element.nativeElement.clientHeight * this.swipeRangeProportionOfSize, this.swipeRangeDistance);
                        const rangeUp: number = Math.min(maxRange, this.swipeMaxDistance || topEdge);
                        const rangeDown: number = Math.min(maxRange, this.swipeMaxDistance || bottomEdge);
                        this.forthRange = this.swipeDirection === Direction.UP ? rangeUp : rangeDown;
                        this.backRange = !this.bidirectional ? 0 : Direction.UP ? rangeDown : rangeUp;
                    } else {
                        const leftEdge: number = this.start.clientX;
                        const rightEdge: number = this.element.nativeElement.clientWidth - this.start.clientX;
                        const maxRange: number = Math.min(this.element.nativeElement.clientWidth * this.swipeRangeProportionOfSize, this.swipeRangeDistance);
                        const rangeLeft: number = Math.min(maxRange, this.swipeMaxDistance || leftEdge);
                        const rangeRight: number = Math.min(maxRange, this.swipeMaxDistance || rightEdge);
                        this.forthRange = this.swipeDirection === Direction.LEFT ? rangeLeft : rangeRight;
                        this.backRange = !this.bidirectional ? 0 : Direction.LEFT ? rangeRight : rangeLeft;
                    }
                }),
                switchMap(() => touchmove$.pipe(
                    tap((event: TouchEvent) => {
                        let distance: number = this.swipeScale * this.calculateDistance(event);

                        // Determine the swipe direction based on the first observed vertical (for UP/DOWN) or horizontal (for LEFT/RIGHT) movement.
                        // Unless bidirectional, terminate on an initial swipe in the opposite direction to that required.
                        if (!this.engaged) {
                            if (distance == 0) {
                                return;
                            }
                            if (distance < 0 && !this.bidirectional) {
                                this.setDistance(0);
                                this.setEngaged(false);
                                swipeend$.next();
                                return;
                            }
                            this.setEngaged(true);
                        }

                        distance = Math.min(Math.max(distance, -this.backRange), this.forthRange);
                        if (this.swipeEase) {
                            distance = distance >= 0
                                ? SwipableDirective.easeInQuadOutQuart(distance, 0, this.forthRange, this.forthRange)
                                : -SwipableDirective.easeInQuadOutQuart(-distance, 0, this.backRange, this.backRange);
                        }
                        this.setDistance(distance);

                        // Prevent any page content from scrolling at the same time.
                        event.preventDefault();
                    }),
                    takeUntil(touchend$.pipe(
                        tap(() => {
                            if (this.swipePrimed) {
                                this.onSwipe.emit(this.distance >= 0 ? this.swipeDirection
                                    : this.swipeDirection === Direction.UP ? Direction.DOWN
                                        : this.swipeDirection === Direction.DOWN ? Direction.UP
                                            : this.swipeDirection === Direction.LEFT ? Direction.RIGHT
                                                : Direction.LEFT);
                                setTimeout(() => this.setDistance(0), this.swipeRestoreDelay);
                            } else {
                                this.setDistance(0);
                            }
                            this.setEngaged(false);
                        }),
                        merge(swipeend$)
                    ))
                )),
                takeUntil(this.swipestop$)
            ).subscribe();
        }
    }

    ngOnDestroy(): void {
        this.swipestop$.next();
    }

    private get swipePrimed(): boolean {
        return this.distance >= this.forthRange * this.swipePrimeProportionOfRange || (this.bidirectional && -this.distance >= this.backRange * this.swipePrimeProportionOfRange);
    }

    private get scrollerAtLimit(): boolean {
        switch (this.swipeDirection) {
            case Direction.DOWN:
                return this.swipeScroller.nativeElement.scrollTop <= 0;
            case Direction.UP:
                return this.swipeScroller.nativeElement.scrollTop + this.swipeScroller.nativeElement.clientHeight >= this.swipeScroller.nativeElement.scrollHeight;
            case Direction.RIGHT:
                return this.swipeScroller.nativeElement.scrollLeft <= 0;
            case Direction.LEFT:
                return this.swipeScroller.nativeElement.scrollLeft + this.swipeScroller.nativeElement.clientWidth >= this.swipeScroller.nativeElement.scrollWidth;
        }
    }

    private calculateDistance(event: TouchEvent) {
        switch (this.swipeDirection) {
            case Direction.DOWN:
                return event.targetTouches[0].screenY - this.start.screenY;
            case Direction.UP:
                return this.start.screenY - event.targetTouches[0].screenY;
            case Direction.RIGHT:
                return event.targetTouches[0].screenX - this.start.screenX;
            case Direction.LEFT:
                return this.start.screenX - event.targetTouches[0].screenX;
        }
    }

    private setEngaged(engaged: boolean): void {
        this.engaged = engaged;
        if (engaged) {
            this.renderer.addClass(this.element.nativeElement, "woa-swipe-engaged");
        } else if (this.swipePrimed) {
            this.renderer.removeClass(this.element.nativeElement, "woa-swipe-engaged");
        } else {
            this.renderer.addClass(this.element.nativeElement, "woa-swipe-disengaging");
            setTimeout(() => {
                this.renderer.removeClass(this.element.nativeElement, "woa-swipe-disengaging");
                this.renderer.removeClass(this.element.nativeElement, "woa-swipe-engaged");
            }, this.swipeDisengageDelay);
        }
    }

    private setDistance(distance: number): void {
        this.distance = distance;
        this.element.nativeElement.style.setProperty("--woa-swipe-distance", distance);
        this.element.nativeElement.style.setProperty("--woa-swipe-x-distance", this.swipeDirection === Direction.RIGHT ? distance : this.swipeDirection === Direction.LEFT ? -distance : 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-y-distance", this.swipeDirection === Direction.DOWN ? distance : this.swipeDirection === Direction.UP ? -distance : 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-proportion", distance >= 0 ? distance / (this.forthRange * this.swipePrimeProportionOfRange) : -distance / (this.backRange * this.swipePrimeProportionOfRange));
        if (this.swipePrimed) {
            this.renderer.addClass(this.element.nativeElement, "woa-swipe-primed");
        } else {
            this.renderer.removeClass(this.element.nativeElement, "woa-swipe-primed");
        }
    }

    private static easeInQuadOutQuart(t: number, b: number, c: number, d: number): number {
        t /= d / 2;
        return t < 1 ? c / 2 * t * t + b : -c / 2 * ((t -= 2) * t * t * t - 2) + b;
    }

    private canStartSwipe(element: Element): boolean {
        return this.swipeEnabled && (this.bidirectional || !this.swipeScroller || this.scrollerAtLimit || !this.isScrollerChild(element));
    }

    private isScrollerChild(element: Element) {
        return element === this.swipeScroller.nativeElement || element && this.isScrollerChild(element.parentElement);
    }
}



// WEBPACK FOOTER //
// ./src/app/ui-elements/swipable.directive.ts